/**
 * Market-Hub Global Bilingual Title & Navigation SSOT Dictionary
 * Standard: SPEC-MARKET_HUB-BILINGUAL-TYPOGRAPHY-AND-NAV-DICTIONARY-001
 * 
 * Rules:
 * 1. Dual-Audience Principle:
 *    - Human PM/User: Clear Chinese focus with primary weight.
 *    - AI Agents (Codex/Gemini/GPT): Stable uppercase English subtitles as immutable DOM locators.
 * 2. Typographic Ratio:
 *    - English font size MUST be 50% - 60% of Chinese font size.
 * 3. Sidebar Rail Invariant:
 *    - Sidebar `sub` MUST be <= 18 characters to strictly prevent ellipsis truncation (`...`).
 * 4. Zero Parentheses Invariant:
 *    - All titles/headers MUST use structured dual lines; single-line parenthetical English (e.g. `(Core-7 Matrix)`) is strictly prohibited.
 */

export interface BilingualEntry {
  zh: string;
  en: string;
  icon?: string;
  badge?: string;
  description?: string;
}

export interface SidebarBilingualItem {
  id: string;
  label: string;
  sub: string; // Strictly <= 18 chars, prevents ellipsis on 200px sidebar
  icon: string;
  hotkeyHint?: string;
  route: string;
}

/**
 * 1. SYSTEM REALM (系统运维与基础设施)
 */
export const SYSTEM_BILINGUAL: Record<string, BilingualEntry> = {
  pipeline: {
    zh: '调度中心与治理流水线',
    en: 'PIPELINE & DISPATCH COCKPIT',
    icon: '🚄',
    badge: 'Pipeline Harmonizer · Active',
    description: 'Market-Hub 多周期后台 Worker 原生节拍观测、无锁化脉冲遥测与全链路生命周期看板',
  },
  infra: {
    zh: '基础设施与数据底座',
    en: 'INFRASTRUCTURE & DATA FOUNDATION',
    icon: '⚙️',
    description: 'Tokyo Oracle VPS 宿主机负载、TimescaleDB 双库隔离时序引擎与全资产健康遥测',
  },
  audit: {
    zh: '灾备审计与对账日志',
    en: 'DR AUDIT & RECONCILIATION LOG',
    icon: '🛡️',
    description: '容灾状态机审计、Dead-Letter Outbox 队列回溯与服务异常日志排查',
  },
  gateway: {
    zh: '开放网关与接地对齐',
    en: 'GATEWAY & ONBOARDING ALIGNMENT',
    icon: '🔌',
    description: '下游系统 (TSO / Doctor-A) 接入契约、只读 API 资产清单与服务治理审计',
  },
  settings: {
    zh: '系统全局设置',
    en: 'GLOBAL SETTINGS & PARAMS',
    icon: '⚙️',
    description: '系统偏好、网络通讯参数与 Cloudflare AI Gateway 智能化运行时集成',
  },
};

/**
 * 2. CRYPTO REALM (加密货币量化态势)
 */
export const CRYPTO_BILINGUAL: Record<string, BilingualEntry> = {
  brief: {
    zh: '加密多空 AI 决策简报中枢',
    en: 'CRYPTO EXECUTIVE AI BRIEF COCKPIT',
    icon: '🧠',
  },
  marketBoard: {
    zh: '多资产全景态势看板',
    en: 'MULTI-ASSET MARKET BOARD',
    icon: '📊',
  },
  weather: {
    zh: '全域趋势气象与时空共振',
    en: 'TREND WEATHER & REGIME HEATMAP',
    icon: '🌤️',
  },
  indicators: {
    zh: '多周期指标流与动能分析',
    en: 'MULTI-TIMEFRAME INDICATOR STREAM',
    icon: '📈',
  },
  feeds: {
    zh: '全球主流标的基差与异动雷达',
    en: 'TICKERS & QUANTITATIVE RADAR',
    icon: '📡',
  },
  regime: {
    zh: '态势决策与形态回测中枢',
    en: 'REGIME SEMANTICS & BACKTEST HUB',
    icon: '🧭',
  },
  strategist: {
    zh: 'AI 首席战术分析官推演档案',
    en: 'AI CHIEF STRATEGIST TACTICAL DOSSIER',
    icon: '♟️',
  },
  tradeAdvisory: {
    zh: '实战交易决策蓝图',
    en: 'TRADE BLUEPRINT',
    icon: '🎯',
    description: 'DeMark 序列、量价节奏预警、正向置信评分与多色风控止损止盈全景决策驾驶舱',
  },
  tacticalDeck: {
    zh: '战术牌面与出战中枢',
    en: 'TACTICAL DECK',
    icon: '♟️',
    description: 'Core-7 标的多周期行情牌面诊断、Volume Profile 筹码重心与 4 大出战阵型决策中枢',
  },
};

/**
 * 3. A-SHARE REALM (中国 A 股大盘)
 */
export const ASHARE_BILINGUAL: Record<string, BilingualEntry> = {
  screener: {
    zh: '大盘全域量化初筛中枢',
    en: 'A-SHARE FULL MARKET SCREENER',
    icon: '🇨🇳',
  },
  watchlist: {
    zh: 'A股核心自选标的极速看板',
    en: 'CORE WATCHLIST HIGH-FREQ FEED',
    icon: '⚡',
  },
  indexRadar: {
    zh: 'A股指数与成份股归因雷达',
    en: 'BENCHMARK INDEX WEIGHT ATTRIBUTION',
    icon: '📊',
  },
  ambushRadar: {
    zh: '潜伏雷达与挂单中枢',
    en: 'LATENT AMBUSH & FIBONACCI PIVOT HUB',
    icon: '🎯',
  },
  ctMatrix: {
    zh: '全市场 CT 体检中枢',
    en: 'MARKET BREADTH & SENTIMENT SCANNER',
    icon: '🩺',
  },
  patternTags: {
    zh: '股票形态标签中枢',
    en: 'PATTERN & FACT TAGGING ENGINE',
    icon: '🏷️',
  },
  sectorRotation: {
    zh: '板块强弱轮动矩阵 & 动量 RRG 罗盘',
    en: 'SECTOR ROTATION & MOMENTUM RRG COMPASS',
    icon: '🔄',
  },
  mainProfile: {
    zh: '主力筹码画像与控盘诊断',
    en: 'MAIN FORCE & CHIP DENSITY PROFILE',
    icon: '👤',
  },
};

/**
 * 4. CARD SECTION TITLES (卡片/区块二级双行小标题)
 */
export const CARD_TITLES_BILINGUAL: Record<string, BilingualEntry> = {
  drAudit: {
    zh: '全域容灾审计与异常日志',
    en: 'DR STATE MACHINE & INTEGRITY AUDIT',
    icon: '🛡️',
  },
  core7Matrix: {
    zh: '核心标的物化矩阵与战术博弈参考带',
    en: 'CORE-7 QUANTITATIVE MATRIX',
  },
  evidenceObservations: {
    zh: '底层客观事实与全景数据观察',
    en: 'OBJECTIVE EVIDENCE OBSERVATIONS',
  },
  topDownWorkflow: {
    zh: '量化核验流水线导航指挥台',
    en: 'TOP-DOWN QUANTITATIVE WORKFLOW',
  },
  tacticalPlaybook: {
    zh: 'AI 首席操盘战术执行单',
    en: 'TACTICAL EXECUTION PLAYBOOK',
  },
  spatioTemporalMatrix: {
    zh: '三维多级别时空推演矩阵',
    en: '3D MULTI-TIMEFRAME SPATIO-TEMPORAL MATRIX',
  },
  analystThesis: {
    zh: 'AI 首席分析师决策意见与失效假设',
    en: 'ANALYST THESIS & INVALIDATION',
  },
  tradeAdvisoryCockpit: {
    zh: '实战交易决策驾驶舱',
    en: 'TRADE ADVISORY COCKPIT',
  },
  actionableGeometry: {
    zh: '入场止损止盈几何空间',
    en: 'ACTIONABLE PRICE GEOMETRY',
  },
  groundingEvidenceDrawer: {
    zh: '底层接地事实与溯源抽屉',
    en: 'GROUNDING EVIDENCE DRAWER',
  },
};

/**
 * 5. SIDEBAR RAIL ITEMS (严格限制英文副标题字符数 <= 18，根除截断省略号)
 */
export const SIDEBAR_ITEMS_BILINGUAL: Record<string, SidebarBilingualItem[]> = {
  system: [
    {
      id: 'view-dispatcher-cockpit',
      label: '1. 全域流水线与调度',
      sub: 'Pipeline Cockpit', // 16 chars <= 18
      icon: '🚄',
      hotkeyHint: '1, d',
      route: '#/system/pipeline',
    },
    {
      id: 'view-infra-telemetry',
      label: '2. 基础设施与节点监控',
      sub: 'Infra & Nodes', // 13 chars <= 18
      icon: '🛡️',
      hotkeyHint: '2',
      route: '#/system/infra',
    },
    {
      id: 'view-dr-audit',
      label: '3. 全域容灾审计与日志',
      sub: 'DR Audit & Logs', // 15 chars <= 18
      icon: '🚨',
      hotkeyHint: '3',
      route: '#/system/audit',
    },
    {
      id: 'view-integrations-audit',
      label: '4. 开放网关与接地对齐',
      sub: 'Gateway & Ground', // 16 chars <= 18
      icon: '🔌',
      hotkeyHint: '4, g',
      route: '#/system/gateway',
    },
    {
      id: 'view-settings',
      label: '0. 系统偏好与AI网关',
      sub: 'Settings & AI GW', // 16 chars <= 18
      icon: '⚙️',
      hotkeyHint: '0',
      route: '#/system/settings',
    },
  ],
  crypto: [
    {
      id: 'view-crypto-brief',
      label: '1. AI 决策简报中枢',
      sub: 'Exec AI Brief', // 13 chars
      icon: '🧠',
      hotkeyHint: '1, b',
      route: '#/crypto/brief',
    },
    {
      id: 'view-market-board-preview',
      label: '2. 多资产市场中枢',
      sub: 'Multi-Asset Board', // 17 chars
      icon: '📊',
      hotkeyHint: '2',
      route: '#/crypto/market-board',
    },
    {
      id: 'view-trend-weather',
      label: '3. 趋势气象中枢',
      sub: 'Trend Weather', // 13 chars
      icon: '🌤️',
      hotkeyHint: '3, w',
      route: '#/weather',
    },
    {
      id: 'view-indicators-matrix',
      label: '4. 多周期指标流中枢',
      sub: 'Indicator Stream', // 16 chars
      icon: '📈',
      hotkeyHint: '4',
      route: '#/indicators',
    },
    {
      id: 'view-market-feeds',
      label: '5. 资产流与量化雷达',
      sub: 'Tickers & Radar', // 15 chars
      icon: '📡',
      hotkeyHint: '5',
      route: '#/feeds',
    },
    {
      id: 'view-regime-matrix',
      label: '6. 态势感知与回测',
      sub: 'Regime & Backtest', // 17 chars
      icon: '🧭',
      hotkeyHint: '6',
      route: '#/regime',
    },
    {
      id: 'view-crypto-strategist',
      label: '7. AI 战术视界 (Shadow)',
      sub: 'Chief Strategist', // 16 chars
      icon: '♟️',
      hotkeyHint: 's',
      route: '#/crypto/strategist',
    },
    {
      id: 'view-trade-advisory',
      label: '8. 实战决策蓝图',
      sub: 'TRADE BLUEPRINT', // 15 chars <= 18
      icon: '🎯',
      hotkeyHint: 'a',
      route: '#/crypto/trade-advisory',
    },
    {
      id: 'view-crypto-tactical-deck',
      label: '9. 战术牌面与出战中枢',
      sub: 'TACTICAL DECK', // 13 chars <= 18
      icon: '♟️',
      hotkeyHint: 't',
      route: '#/crypto/tactical-deck',
    },
  ],
  ashare: [
    {
      id: 'view-ashare-screener',
      label: '1. 全域选股中枢',
      sub: 'A-Share Screener', // 16 chars
      icon: '🇨🇳',
      hotkeyHint: '1',
      route: '#/ashare/screener',
    },
    {
      id: 'view-ashare-watchlist',
      label: '2. 核心自选看板',
      sub: 'Watchlist Feed', // 14 chars
      icon: '⚡',
      hotkeyHint: '2',
      route: '#/ashare/watchlist',
    },
    {
      id: 'view-ashare-index-radar',
      label: '3. 指数归因雷达',
      sub: 'Index Attribution', // 17 chars
      icon: '📊',
      hotkeyHint: '3',
      route: '#/ashare/index-radar',
    },
    {
      id: 'view-ashare-ambush-radar',
      label: '4. 潜伏挂单中枢',
      sub: 'Ambush & Pivot', // 14 chars
      icon: '🎯',
      hotkeyHint: '4',
      route: '#/ashare/ambush-radar',
    },
    {
      id: 'view-ashare-ct-matrix',
      label: '5. 全市场CT体检',
      sub: 'Market Health CT', // 16 chars
      icon: '🩺',
      hotkeyHint: '5',
      route: '#/ashare/ct-matrix',
    },
    {
      id: 'view-ashare-pattern-tags',
      label: '6. 形态标签中枢',
      sub: 'Pattern Tagging', // 15 chars
      icon: '🏷️',
      hotkeyHint: '6',
      route: '#/ashare/pattern-tags',
    },
    {
      id: 'view-ashare-sector-rotation',
      label: '7. 板块轮动矩阵',
      sub: 'Sector Momentum', // 15 chars
      icon: '🔄',
      hotkeyHint: '7',
      route: '#/ashare/sector-rotation',
    },
    {
      id: 'view-ashare-main-profile',
      label: '8. 主力筹码画像',
      sub: 'Chip Density', // 12 chars
      icon: '👤',
      hotkeyHint: '8',
      route: '#/ashare/main-profile',
    },
  ],
  macro: [
    {
      id: 'view-macro-regime',
      label: '1. 全球宏观情势罗盘',
      sub: 'Macro Regime', // 12 chars
      icon: '🧭',
      hotkeyHint: '1',
      route: '#/macro/regime',
    },
    {
      id: 'view-macro-market',
      label: '2. 15 大类跨资产走廊',
      sub: 'Global Corridor', // 15 chars
      icon: '🌐',
      hotkeyHint: '2',
      route: '#/macro/corridor',
    },
  ],
  backtest: [
    {
      id: 'view-backtest-crypto',
      label: '1. 加密货币回测',
      sub: 'Crypto Backtest', // 15 chars
      icon: '🪙',
      hotkeyHint: '1',
      route: '#/backtest/crypto',
    },
    {
      id: 'view-backtest-ashare',
      label: '2. 中国A股回测',
      sub: 'A-Share Backtest', // 16 chars
      icon: '🇨🇳',
      hotkeyHint: '2',
      route: '#/backtest/ashare',
    },
    {
      id: 'view-backtest-usequity',
      label: '3. 美股回测',
      sub: 'US Equity Lab', // 13 chars
      icon: '🇺🇸',
      hotkeyHint: '3',
      route: '#/backtest/usequity',
    },
    {
      id: 'view-backtest-macro',
      label: '4. 宏观大宗回测',
      sub: 'Macro Corridor', // 14 chars
      icon: '🌐',
      hotkeyHint: '4',
      route: '#/backtest/macro',
    },
  ],
};
